import * as Sentry from '@sentry/react';

// "Network Error" vs 401 vs timeout vs ECONNABORTED must group as separate,
// countable issues in Sentry rather than collapsing into one bucket.
export const errorKindTags = (err) => ({
  error_code: err?.code || 'none',
  status: err?.response?.status ?? 'no_response',
});

// The backend returns its full envelope (message, formatting_args, ...) on failure.
// Forward it rather than rebuilding a message-only object — rebuilding drops
// formatting_args, which is the i18n interpolation slot.
//
// The typeof guard is required: a 502 returns an HTML *string*, and destructuring
// { error, message } off a string yields undefined for both, so `if (!error)` would
// pass and a gateway failure would read as success.
//
// The fallback must never be err.message — on the PWA/offline path there is no
// err.response at all and err.message is the literal string "Network Error".
export const errorEnvelope = (
  err,
  fallbackMessage = 'Something went wrong. Please try again.',
) => {
  const body = err?.response?.data;
  return body && typeof body === 'object'
    ? { ...body, error: true }
    : { error: true, message: fallbackMessage };
};

// Bind the module tag once at the top of an action file so call sites stay short:
//   const captureActionError = createActionErrorCapture('inventory');
//   captureActionError(err, 'GetCompanyWiseInventoryData');
export const createActionErrorCapture = (module) => (err, flow, extra) =>
  Sentry.captureException(err, {
    tags: { module, flow, ...errorKindTags(err) },
    ...(extra ? { extra } : {}),
  });

// PII-safe variant. A raw AxiosError carries err.config with headers.Authorization
// (the live bearer token) and the full request body — for the OCR/beneficiary
// endpoints that means Aadhaar/PAN images and staff PII. Send a clean Error and
// only the fields that are safe to keep.
export const createScrubbedErrorCapture = (module) => (err, flow) => {
  const scrubbed = new Error(
    err?.response?.data?.message || err?.message || 'Request failed',
  );
  scrubbed.name = err?.name || 'Error';
  Sentry.captureException(scrubbed, {
    tags: { module, flow, ...errorKindTags(err) },
    extra: {
      status: err?.response?.status,
      url: err?.config?.url,
      method: err?.config?.method,
    },
  });
};
